import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  LogOut, Menu, Loader2, DollarSign, LayoutDashboard,
  Workflow, BarChart3, Activity, Store, Shield, Building2,
  CheckCircle2, AlertTriangle, ExternalLink, XCircle, Plus
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Revenue = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [revenue, setRevenue] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showCreateContract, setShowCreateContract] = useState(false);
  const [contractForm, setContractForm] = useState({ business_id: '', operator_id: '', agreed_monthly_price: 1100, description: '' });
  const [businesses, setBusinesses] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [revRes, statusRes, bizRes] = await Promise.all([
        axios.get(`${API}/connect/revenue/operator`),
        axios.get(`${API}/connect/operators/stripe-status`),
        axios.get(`${API}/businesses`),
      ]);
      setRevenue(revRes.data);
      setStripeStatus(statusRes.data);
      setBusinesses(bizRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/connect/operators/connect-stripe`, {
        return_url: `${window.location.origin}/revenue`,
        refresh_url: `${window.location.origin}/revenue`,
      });
      if (res.data.onboarding_url) {
        window.open(res.data.onboarding_url, '_blank');
        toast.success('Complete onboarding in the new tab');
      } else if (res.data.status === 'already_connected') {
        toast.info('Already connected to Stripe');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Connection failed'); }
    finally { setConnecting(false); }
  };

  const handleCreateContract = async () => {
    if (!contractForm.business_id || !contractForm.agreed_monthly_price) return toast.error('Business and price required');
    try {
      await axios.post(`${API}/connect/contracts/create`, { ...contractForm, operator_id: user.id });
      toast.success('Contract created');
      setShowCreateContract(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCancelContract = async (id) => {
    if (!window.confirm('Cancel this contract?')) return;
    try {
      await axios.post(`${API}/connect/contracts/${id}/cancel`, { reason: 'Operator cancelled' });
      toast.success('Contract cancelled');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: DollarSign, label: 'Revenue', path: '/revenue' },
    { icon: Building2, label: 'Businesses', path: '/businesses' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: Activity, label: 'Agent Monitor', path: '/agents' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5"><Link to="/" className="flex items-center gap-2"><HSLogo size={32} /><span className="font-black text-lg tracking-tight">HumanScaled</span></Link></div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(item => { const Icon = item.icon; const isActive = location.pathname === item.path; return (<Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}><Icon className="w-5 h-5" />{item.label}</Link>); })}
          </nav>
          <div className="p-4 border-t border-white/5"><Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button></div>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="revenue-title">Revenue & Billing</h1>
            </div>
            <Button onClick={() => setShowCreateContract(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-9 text-sm" data-testid="new-contract-btn"><Plus className="w-4 h-4 mr-1" /> New Contract</Button>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> : (
            <>
              {/* Stripe Status */}
              <div className={`border p-5 flex items-center justify-between ${stripeStatus?.onboarding_completed ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-amber-500/20 bg-amber-500/5'}`} data-testid="stripe-status">
                <div className="flex items-center gap-3">
                  {stripeStatus?.onboarding_completed ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <AlertTriangle className="w-5 h-5 text-amber-400" />}
                  <div>
                    <p className="font-medium text-sm">{stripeStatus?.message}</p>
                    <p className="text-xs text-muted-foreground">Platform fee: {revenue?.platform_fee_percent || 5}% · You keep {100 - (revenue?.platform_fee_percent || 5)}%</p>
                  </div>
                </div>
                {!stripeStatus?.onboarding_completed && (
                  <Button onClick={handleConnectStripe} disabled={connecting} className="bg-violet-600 text-white hover:bg-violet-500 rounded-sm h-9 text-sm" data-testid="connect-stripe-btn">
                    {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-1" /> Connect Stripe</>}
                  </Button>
                )}
              </div>

              {/* Revenue Cards */}
              {revenue && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="revenue-cards">
                  <div className="bg-[#0a0a0a] border border-white/5 p-5">
                    <p className="text-[10px] text-muted-foreground uppercase">Active Contracts</p>
                    <p className="font-mono text-2xl font-bold text-white">{revenue.active_contracts}</p>
                  </div>
                  <div className="bg-[#0a0a0a] border border-white/5 p-5">
                    <p className="text-[10px] text-muted-foreground uppercase">Monthly Revenue</p>
                    <p className="font-mono text-2xl font-bold text-emerald-400">${revenue.monthly_revenue?.toLocaleString()}</p>
                  </div>
                  <div className="bg-[#0a0a0a] border border-white/5 p-5">
                    <p className="text-[10px] text-muted-foreground uppercase">Your Payout</p>
                    <p className="font-mono text-2xl font-bold text-emerald-400">${revenue.monthly_payout?.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">{100 - revenue.platform_fee_percent}% after fees</p>
                  </div>
                  <div className="bg-[#0a0a0a] border border-white/5 p-5">
                    <p className="text-[10px] text-muted-foreground uppercase">Annual Projected</p>
                    <p className="font-mono text-2xl font-bold text-white">${revenue.annual_projected?.toLocaleString()}</p>
                  </div>
                </div>
              )}

              {/* Contracts */}
              <div data-testid="contracts-list">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Contracts</h2>
                {revenue?.contracts?.length > 0 ? (
                  <div className="space-y-3">
                    {revenue.contracts.map(c => (
                      <div key={c.id} className="bg-[#0a0a0a] border border-white/5 p-5 flex items-center justify-between" data-testid={`contract-${c.id}`}>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-sm">{c.business_name}</h3>
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{c.status}</span>
                          </div>
                          <div className="flex gap-4 text-xs text-muted-foreground">
                            <span className="font-mono">${c.agreed_monthly_price}/mo</span>
                            <span>→ You get: <span className="text-emerald-400 font-mono">${c.operator_payout}/mo</span></span>
                            <span>Fee: <span className="text-amber-400 font-mono">{c.platform_fee_percent}%</span></span>
                          </div>
                        </div>
                        {c.status === 'active' && (
                          <Button onClick={() => handleCancelContract(c.id)} variant="outline" size="sm" className="border-red-500/20 text-red-400 rounded-sm h-7 text-xs">Cancel</Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-[#0a0a0a] border border-white/5 p-8 text-center">
                    <DollarSign className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-30" />
                    <p className="text-sm text-muted-foreground">No contracts yet. Create one to start earning.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Create Contract Modal */}
      <Dialog open={showCreateContract} onOpenChange={setShowCreateContract}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
          <DialogHeader><DialogTitle>Create Contract</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Business</label>
              <select value={contractForm.business_id} onChange={e => setContractForm({ ...contractForm, business_id: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-sm p-2 text-sm text-white" data-testid="contract-business">
                <option value="">Select business</option>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Monthly Price ($)</label>
              <Input type="number" value={contractForm.agreed_monthly_price} onChange={e => setContractForm({ ...contractForm, agreed_monthly_price: parseFloat(e.target.value) || 0 })} className="bg-black/50 border-white/10 rounded-sm" data-testid="contract-price" />
              <p className="text-[10px] text-muted-foreground mt-1">
                You receive: <span className="text-emerald-400 font-mono">${(contractForm.agreed_monthly_price * 0.95).toFixed(2)}</span> · Platform fee: <span className="text-amber-400 font-mono">${(contractForm.agreed_monthly_price * 0.05).toFixed(2)}</span>
              </p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Description</label>
              <Input value={contractForm.description} onChange={e => setContractForm({ ...contractForm, description: e.target.value })} placeholder="AI scheduling service" className="bg-black/50 border-white/10 rounded-sm" />
            </div>
            <Button onClick={handleCreateContract} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="create-contract-submit">Create Contract & Start Subscription</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Revenue;
